import dayjs from 'dayjs'
import { useMemo } from 'react'
import { z } from 'zod'
import { t } from '@lingui/macro'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCreateExpertiseMeetingMutation } from '@/core/services/expertise-meeting'
import { getApiErrorMessage } from '@/utils/errors'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'

function useFormSchema() {
  return useMemo(
    () =>
      z.object({
        title: z.string().min(1),
        date: z.string().min(1),
        time: z.string().regex(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm, { message: t`Invalid time` }),
      }),
    []
  )
}

export type PlanMeetingFormData = z.infer<ReturnType<typeof useFormSchema>>

export function usePlanMeetingForm({ expertiseId, patientId }: { expertiseId: string; patientId: string }) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [createExpertiseMeeting, { isLoading: isCreatingExpertiseMeeting }] = useCreateExpertiseMeetingMutation()

  const schema = useFormSchema()
  const rhForm = useForm<PlanMeetingFormData>({
    defaultValues: {
      date: dayjs().format('YYYY-MM-DD'),
      time: dayjs().add(1, 'hour').startOf('hour').format('HH:mm'),
    },
    resolver: zodResolver(schema),
  })

  const onSubmit: SubmitHandler<PlanMeetingFormData> = async (data) => {
    try {
      const timestamp = dayjs(`${data.date} ${data.time}`).toISOString()
      const expertiseMeeting = await createExpertiseMeeting({ expertise: expertiseId, patient: patientId, title: data.title, timestamp }).unwrap()
      navigate(`/expertise-meetings/${expertiseMeeting._id}`)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(t`An error has occured while planning the meeting. Please try again!`), { variant: 'error' })
    }
  }

  return { isCreatingExpertiseMeeting, rhForm, onSubmit }
}
